import { useQuery } from '@apollo/client';
import { Loader2, Ban, List, LogOut, ParkingSquare } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ParkingSessionDisplayItem } from '@/components/ui/parking-session-display-item';
import { SearchInput } from '@/components/ui/search-input';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import { useDebounce } from '@/hooks/useDebounce';
import { CancelReason, ParkingSessionStatus } from '@/graphql/generated';

import { PARKING_SESSIONS_LIST_QUERY } from './parking-sessions.gql';
import {
	calculateDuration,
	formatCustomerName,
	getParkingSessionDisplayStatus
} from './utils';
import type { ParkingSessionDisplayStatus } from './types';

const PARKING_SESSIONS_PANEL_PAGE_SIZE = 100;

interface ParkingSessionNode {
	parkingSessionId: string;
	ticketId?: string | null;
	entryDateTime: string;
	exitDateTime?: string | null;
	paymentAmount?: number | null;
	customerId?: string | null;
	vehicleId?: string | null;
	parkingPositionId?: string | null;
	contractId?: string | null;
	status: ParkingSessionStatus;
	cancelReason?: CancelReason | null;
	vehicle?: {
		vehicleId: string;
		plateNumber?: string | null;
		make?: string | null;
		model?: string | null;
		color?: string | null;
		vehicleType?: {
			name?: string | null;
		} | null;
	} | null;
	customer?: {
		customerId: string;
		firmName?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		isCompany?: boolean | null;
	} | null;
	parkingPosition?: {
		parkingPositionId: string;
		code?: string | null;
	} | null;
}

interface ParkingSessionsListPanelQueryResponse {
	parkingSessions?: {
		nodes?: ParkingSessionNode[] | null;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		} | null;
	} | null;
}

interface ParkingSessionPanelListItem {
	parkingSessionId: string;
	ticketId?: string | null;
	entryDateTime: string;
	exitDateTime?: string | null;
	paymentAmount?: number | null;
	vehiclePlateNumber?: string | null;
	customerName?: string | null;
	parkingPositionCode?: string | null;
	durationMinutes?: number | null;
	displayStatus?: ParkingSessionDisplayStatus;
}

interface ParkingSessionsListPanelProps {
	vehicleId?: string;
	customerId?: string;
	contractId?: string;
	isActive?: boolean;
	emptyMessage?: string;
	className?: string;
}

const STATUS_ALL = '';
const DEFAULT_STATUS_FILTER = STATUS_ALL;

interface ParkingSessionsPanelFiltersState {
	selectedStatus: string;
	selectedDate: string;
	searchText: string;
}

const defaultParkingSessionsPanelFiltersState: ParkingSessionsPanelFiltersState = {
	selectedStatus: DEFAULT_STATUS_FILTER,
	selectedDate: '',
	searchText: ''
};

// Keeps filters stable when the details pane switches between entities.
let persistedParkingSessionsPanelFiltersState: ParkingSessionsPanelFiltersState = {
	...defaultParkingSessionsPanelFiltersState
};

export function ParkingSessionsListPanel({
	vehicleId,
	customerId,
	contractId,
	isActive = true,
	emptyMessage,
	className
}: ParkingSessionsListPanelProps) {
	const { t } = useTranslation('common');
	const { t: tPS } = useTranslation('parkingSessions');
	const [selectedStatus, setSelectedStatus] =
		useState<string>(() => persistedParkingSessionsPanelFiltersState.selectedStatus);
	const [selectedDate, setSelectedDate] = useState(
		() => persistedParkingSessionsPanelFiltersState.selectedDate
	);
	const [searchText, setSearchText] = useState(
		() => persistedParkingSessionsPanelFiltersState.searchText
	);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const debouncedSearchText = useDebounce(searchText, 500);
	const debouncedSelectedStatus = useDebounce(selectedStatus, 500);

	useEffect(() => {
		persistedParkingSessionsPanelFiltersState = {
			selectedStatus,
			selectedDate,
			searchText
		};
	}, [selectedStatus, selectedDate, searchText]);

	const statusOptions = useMemo(
		() => [
			{
				value: STATUS_ALL,
				label: tPS('list.filters.all'),
				icon: <List className="h-4 w-4 text-gray-500" />
			},
			{
				value: ParkingSessionStatus.Inside,
				label: tPS('list.filters.inside'),
				icon: (
					<ParkingSquare className="h-4 w-4 text-blue-600 dark:text-blue-400" />
				)
			},
			{
				value: ParkingSessionStatus.Exited,
				label: tPS('list.filters.exited'),
				icon: <LogOut className="h-4 w-4 text-gray-500 dark:text-gray-400" />
			},
			{
				value: ParkingSessionStatus.Canceled,
				label: tPS('list.filters.canceled'),
				icon: <Ban className="h-4 w-4 text-red-500 dark:text-red-400" />
			}
		],
		[tPS]
	);

	const hasActiveFilters = useMemo(
		() =>
			searchText !== '' ||
			selectedDate !== '' ||
			selectedStatus !== DEFAULT_STATUS_FILTER,
		[searchText, selectedDate, selectedStatus]
	);

	const baseWhere = useMemo(() => {
		const relationFilters: Record<string, unknown>[] = [];

		if (vehicleId) {
			relationFilters.push({ vehicleId: { eq: vehicleId } });
		}

		if (customerId) {
			relationFilters.push({ customerId: { eq: customerId } });
		}

		if (contractId) {
			relationFilters.push({ contractId: { eq: contractId } });
		}

		if (relationFilters.length === 0) {
			return undefined;
		}

		if (relationFilters.length === 1) {
			return relationFilters[0];
		}

		return { and: relationFilters };
	}, [vehicleId, customerId, contractId]);

	const where = useMemo(() => {
		const conditions: Record<string, unknown>[] = [];

		if (baseWhere) {
			conditions.push(baseWhere);
		}

		if (selectedDate) {
			const startOfDay = new Date(selectedDate);
			startOfDay.setHours(0, 0, 0, 0);
			const endOfDay = new Date(selectedDate);
			endOfDay.setHours(23, 59, 59, 999);

			conditions.push({
				entryDateTime: {
					gte: startOfDay.toISOString(),
					lte: endOfDay.toISOString()
				}
			});
		}

		if (debouncedSearchText) {
			conditions.push({
				or: [
					{
						ticketId: {
							contains: debouncedSearchText
						}
					},
					{
						vehicle: {
							plateNumber: {
								contains: debouncedSearchText
							}
						}
					}
				]
			});
		}

		if (debouncedSelectedStatus && debouncedSelectedStatus !== STATUS_ALL) {
			conditions.push({
				status: {
					eq: debouncedSelectedStatus
				}
			});
		}

		if (conditions.length === 0) {
			return undefined;
		}

		if (conditions.length === 1) {
			return conditions[0];
		}

		return {
			and: conditions
		};
	}, [baseWhere, selectedDate, debouncedSearchText, debouncedSelectedStatus]);

	const {
		data,
		loading,
		error,
		fetchMore,
		refetch
	} = useQuery<ParkingSessionsListPanelQueryResponse>(
		PARKING_SESSIONS_LIST_QUERY,
		{
			variables: {
				first: PARKING_SESSIONS_PANEL_PAGE_SIZE,
				where
			},
			skip: !isActive || selectedStatus !== debouncedSelectedStatus,
			fetchPolicy: 'cache-and-network',
			errorPolicy: 'all'
		}
	);

	const hasNextPage = data?.parkingSessions?.pageInfo?.hasNextPage ?? false;
	const endCursor = data?.parkingSessions?.pageInfo?.endCursor;

	const items = useMemo(() => {
		const nodes = data?.parkingSessions?.nodes ?? [];
		return nodes
			.filter((node): node is ParkingSessionNode => Boolean(node))
			.map<ParkingSessionPanelListItem>(node => ({
				parkingSessionId: node.parkingSessionId,
				ticketId: node.ticketId,
				entryDateTime: node.entryDateTime,
				exitDateTime: node.exitDateTime,
				paymentAmount: node.paymentAmount,
				vehiclePlateNumber: node.vehicle?.plateNumber,
				customerName:
					formatCustomerName(
						node.customer?.isCompany,
						node.customer?.firmName,
						node.customer?.firstName,
						node.customer?.lastName
					) ?? null,
				parkingPositionCode: node.parkingPosition?.code ?? null,
				durationMinutes: calculateDuration(
					node.entryDateTime,
					node.exitDateTime
				),
				displayStatus: getParkingSessionDisplayStatus(node.status)
			}));
	}, [data?.parkingSessions?.nodes]);

	const handleClearFilters = useCallback(() => {
		setSearchText(defaultParkingSessionsPanelFiltersState.searchText);
		setSelectedDate(defaultParkingSessionsPanelFiltersState.selectedDate);
		setSelectedStatus(defaultParkingSessionsPanelFiltersState.selectedStatus);
	}, []);

	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) {
			return;
		}

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					first: PARKING_SESSIONS_PANEL_PAGE_SIZE,
					after: endCursor,
					where
				},
				updateQuery: (previousResult, { fetchMoreResult }) => {
					if (!fetchMoreResult?.parkingSessions) {
						return previousResult;
					}

					return {
						...previousResult,
						parkingSessions: {
							...fetchMoreResult.parkingSessions,
							nodes: [
								...(previousResult.parkingSessions?.nodes ?? []),
								...(fetchMoreResult.parkingSessions.nodes ?? [])
							]
						}
					};
				}
			});
		} finally {
			setIsLoadingMore(false);
		}
	}, [fetchMore, hasNextPage, isLoadingMore, endCursor, where]);

	if (!isActive) {
		return null;
	}

	return (
		<div className={className}>
			<div className="mb-3 py-3 px-3 -mx-1 rounded-lg bg-gray-50 dark:bg-slate-800/50">
				<div className="flex flex-wrap gap-2">
				<div className="w-44">
					<SearchInput
						value={searchText}
						onChange={event => setSearchText(event.target.value)}
						placeholder={tPS('list.searchPlaceholder')}
						variant="transparent"
						className="h-8 text-xs"
					/>
				</div>
				<div className="w-36">
					<Input
						type="date"
						value={selectedDate}
						onChange={event => setSelectedDate(event.target.value)}
						variant="transparent"
						className="h-8 text-xs"
					/>
				</div>
				<div className="w-36">
					<SentryDropdown
						options={statusOptions}
						value={selectedStatus}
						onChange={value => setSelectedStatus(String(value))}
						placeholder={tPS('list.filters.all')}
						groupByCategory={false}
						className="h-8 w-full text-xs"
						variant="transparent"
					/>
				</div>
				{hasActiveFilters && (
					<Button
						variant="outline"
						size="sm"
						onClick={handleClearFilters}
						className="h-8 text-xs"
					>
						{tPS('list.clearFilters')}
					</Button>
				)}
				</div>
			</div>

			{error ? (
				<div className="flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
					<span>{error.message || t('messages.error')}</span>
					<Button
						size="sm"
						variant="outline"
						onClick={() =>
							void refetch({
								first: PARKING_SESSIONS_PANEL_PAGE_SIZE,
								where
							})
						}
					>
						{t('buttons.refresh')}
					</Button>
				</div>
			) : null}

			{!error && !loading && items.length === 0 ? (
				<div className="py-6 text-sm text-muted-foreground">
					{emptyMessage ?? t('messages.no_data')}
				</div>
			) : (
				<div className="divide-y divide-border text-sm">
					{items.map(item => (
						<div
							key={item.parkingSessionId}
							className="py-3 first:pt-0 last:pb-0"
						>
							<ParkingSessionDisplayItem
								parkingSessionId={item.parkingSessionId}
								vehiclePlateNumber={item.vehiclePlateNumber}
								ticketId={item.ticketId}
								entryDateTime={item.entryDateTime}
								exitDateTime={item.exitDateTime}
								durationMinutes={item.durationMinutes}
								paymentAmount={item.paymentAmount}
								displayStatus={item.displayStatus}
								customerName={item.customerName}
								parkingPositionCode={item.parkingPositionCode}
							/>
						</div>
					))}
				</div>
			)}

			<div className="mt-3 flex items-center justify-end gap-4 border-t border-gray-200 pt-2 dark:border-slate-700">
				<span className="text-sm text-muted-foreground">
					{t('pagination.viewingCount', {
						count: items.length,
						entityName: tPS('entityName')
					})}
				</span>
				{hasNextPage && (
					<button
						type="button"
						onClick={() => void handleLoadMore()}
						disabled={isLoadingMore}
						className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
					>
						{isLoadingMore && <Loader2 className="h-3 w-3 animate-spin" />}
						({t('pagination.loadMore')})
					</button>
				)}
			</div>
		</div>
	);
}
